import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { FaPlay, FaStar, FaEye, FaCalendarAlt, FaArrowLeft, FaMapMarkerAlt, FaLock } from 'react-icons/fa';
import { getComedianteById } from '../../http/Actions/ComedianteAction';
import sinFoto from '../../../images/sin-foto.png';

const ComediantePerfilPage = () => {
  const location = useLocation();
  const comedianteId = location.pathname.split('/').pop();
  
  const [comediante, setComediante] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('videos');

  const token = localStorage.getItem("token");

  const handleSetComediante = (response) => {
    if (response && response.data) {
      setComediante(response.data);
    } else {
      setComediante(response);
    }
  };

  useEffect(() => {
    const fetchComediante = async () => {
      try {
        setIsLoading(true);
        const result = await getComedianteById(comedianteId, handleSetComediante, setError, token);
      } catch (error) {
        console.error("Error al cargar comediante:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (comedianteId) {
      fetchComediante();
    }
  }, [comedianteId, token]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-CL', options);
  };

  // Obtener las listas directamente del backend
  const videosPublicos = comediante?.videoShows || [];
  const videosPrivados = comediante?.videosPrivados || [];
  
  // La pestaña aparece solo si hay videos privados en la respuesta
  const mostrarPestanaPrivados = videosPrivados.length > 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>{error}</p>
          <Link to="/comediantes" className="mt-4 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors">
            Volver a comediantes
          </Link>
        </div>
      </div>
    );
  }

  if (!comediante) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="bg-yellow-500/20 border border-yellow-500 text-yellow-100 px-6 py-4 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-2">Comediante no encontrado</h2>
          <p>No se encontró información para este comediante.</p>
          <Link to="/comediantes" className="mt-4 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors">
            Volver a comediantes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-16">
      {/* Cabecera con imagen de fondo y perfil */}
      <div className="relative mt-8">
        {/* Botón de volver */}
        <div className="absolute top-4 left-4 z-10">
          <Link to="/comediantes" className="flex items-center bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-black/70 transition-all">
            <FaArrowLeft className="mr-2" />
            Volver
          </Link>
        </div>

        {/* Contenido principal */}
        <div className="container mx-auto px-4 relative h-full flex items-end">
          <div className="flex flex-col md:flex-row items-end md:items-center gap-6 mb-8">
            {/* Foto del comediante */}
            <div className="w-40 h-40 rounded-xl overflow-hidden border-4 border-zinc-800 shadow-2xl">
            <img 
              src={
                comediante.imagen && comediante.imagen.trim() !== ''
                  ? comediante.imagen
                  : sinFoto
              }
              alt={comediante.nombre} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = sinFoto;
              }}
            />

            </div>
            
            {/* Nombre y detalles */}
            <div>
              <h1 className="text-4xl font-bold text-white">{comediante.nombre}</h1>
              <div className="flex items-center mt-2">
                <span className="text-gray-300 flex items-center">
                  <FaPlay className="mr-1 text-red-500" />
                  {videosPublicos.length} videos
                </span>
                <span className="mx-2 text-gray-500">•</span>
                <span className="text-gray-300 flex items-center">
                  <FaEye className="mr-1 text-gray-400" />
                  {[...videosPublicos, ...videosPrivados].reduce((total, video) => total + video.vistas, 0)} vistas
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          {/* Información personal */}
          <div className="lg:col-span-1">
            <div className="bg-zinc-800 rounded-xl p-6 h-full shadow-lg">
              <h2 className="text-2xl font-bold text-white mb-4">Biografía</h2>
              <p className="text-gray-300 leading-relaxed">{comediante.biografia}</p>
              
              {/* Estadísticas */}
              <div className="mt-8 pt-6 border-t border-zinc-700">
                <h3 className="text-xl font-bold text-white mb-4">Estadísticas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-zinc-700/50 rounded-lg p-4 text-center">
                    <span className="block text-2xl font-bold text-white">
                      {videosPublicos.length}
                    </span>
                    <span className="text-gray-400 text-sm">Videos</span>
                  </div>
                  <div className="bg-zinc-700/50 rounded-lg p-4 text-center">
                    <span className="block text-2xl font-bold text-white">
                      {[...videosPublicos, ...videosPrivados].reduce((total, video) => total + video.vistas, 0)}
                    </span>
                    <span className="text-gray-400 text-sm">Vistas totales</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Pestañas de contenido */}
          <div className="lg:col-span-2">
            {/* Navegación de pestañas */}
            <div className="flex border-b border-zinc-700 mb-6">
              <button 
                className={`px-6 py-3 font-medium ${activeTab === 'videos' 
                  ? 'text-red-500 border-b-2 border-red-500' 
                  : 'text-gray-400 hover:text-white'}`}
                onClick={() => setActiveTab('videos')}
              >
                Videos Públicos ({videosPublicos.length})
              </button>
              
              {/* Solo mostrar si hay videos privados en la respuesta */}
              {mostrarPestanaPrivados && (
                <button 
                  className={`px-6 py-3 font-medium flex items-center ${activeTab === 'privados' 
                    ? 'text-amber-500 border-b-2 border-amber-500' 
                    : 'text-gray-400 hover:text-white'}`}
                  onClick={() => setActiveTab('privados')}
                >
                  <FaLock className="mr-2 text-sm" />
                  Videos Privados ({videosPrivados.length})
                </button>
              )}
              
              <button 
                className={`px-6 py-3 font-medium ${activeTab === 'eventos' 
                  ? 'text-red-500 border-b-2 border-red-500' 
                  : 'text-gray-400 hover:text-white'}`}
                onClick={() => setActiveTab('eventos')}
              >
                Eventos
              </button>
              <button 
                className={`px-6 py-3 font-medium ${activeTab === 'livestreams' 
                  ? 'text-red-500 border-b-2 border-red-500' 
                  : 'text-gray-400 hover:text-white'}`}
                onClick={() => setActiveTab('livestreams')}
              >
                Livestreams
              </button>
            </div>
            
            {/* Pestaña de Videos Públicos */}
            {activeTab === 'videos' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {videosPublicos.length > 0 ? (
                  videosPublicos.map((video) => (
                    <Link to={`/contenido/ver/${video.videoShowId}`} key={video.videoShowId} className="group">
                      <div className="bg-zinc-800 hover:bg-zinc-700 rounded-xl overflow-hidden transition-all duration-300 shadow-lg group-hover:shadow-red-500/10">
                        <div className="relative aspect-video">
                          <img 
                            src={video.imagen} 
                            alt={video.titulo} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/640x360?text=Sin+Imagen';
                            }}
                          />
                          {/* Overlay de play */}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                            <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                              <FaPlay className="text-white text-2xl ml-1" />
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="text-white text-lg font-bold mb-2 group-hover:text-red-500 transition-colors">
                            {video.titulo}
                          </h3>
                          <div className="flex justify-between text-sm text-gray-400">
                            <span className="flex items-center">
                              <FaCalendarAlt className="mr-1" />
                              {formatDate(video.fechaEstreno)}
                            </span>
                            <div className="flex items-center space-x-3">
                              <span className="flex items-center">
                                <FaEye className="mr-1" />
                                {video.vistas}
                              </span>
                                                             <span className="flex items-center">
  <FaStar className="mr-1 text-yellow-500" />
  {video.totalLikes} Likes
</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-12 bg-zinc-800/50 rounded-xl">
                    <p className="text-gray-400">No hay videos públicos disponibles</p>
                  </div>
                )}
              </div>
            )}

            {/* Pestaña de Videos Privados */}
            {activeTab === 'privados' && mostrarPestanaPrivados && (
              <div>
                {/* Mensaje informativo */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <FaLock className="text-amber-500 mr-2" />
                    <span className="text-amber-100 text-sm">
                      Estos videos son privados y solo pueden ser vistos por el comediante y administradores.
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {videosPrivados.length > 0 ? (
                    videosPrivados.map((video) => (
                      <Link to={`/contenido/ver/${video.videoShowId}`} key={video.videoShowId} className="group">
                        <div className="bg-zinc-800 hover:bg-zinc-700 rounded-xl overflow-hidden transition-all duration-300 shadow-lg group-hover:shadow-amber-500/10 border border-amber-500/20">
                          <div className="relative aspect-video">
                            <img 
                              src={video.imagen} 
                              alt={video.titulo} 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/640x360?text=Sin+Imagen';
                              }}
                            />
                            
                            {/* Indicador de video privado */}
                            <div className="absolute top-2 right-2">
                              <div className="bg-amber-500 text-black text-xs px-2 py-1 rounded-full flex items-center">
                                <FaLock className="mr-1" />
                                PRIVADO
                              </div>
                            </div>
                            
                            {/* Overlay de play */}
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <div className="w-16 h-16 rounded-full bg-amber-600 flex items-center justify-center">
                                <FaPlay className="text-white text-2xl ml-1" />
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-white text-lg font-bold group-hover:text-amber-500 transition-colors">
                                {video.titulo}
                              </h3>
                              <FaLock className="text-amber-500 text-sm" />
                            </div>
                            <div className="flex justify-between text-sm text-gray-400">
                              <span className="flex items-center">
                                <FaCalendarAlt className="mr-1" />
                                {formatDate(video.fechaEstreno)}
                              </span>
                              <div className="flex items-center space-x-3">
                                <span className="flex items-center">
                                  <FaEye className="mr-1" />
                                  {video.vistas}
                                </span>
                                <span className="flex items-center">
                                  <FaStar className="mr-1 text-yellow-500" />
                                  {video.totalLikes} Likes
                                </span>

                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-12 bg-zinc-800/50 rounded-xl">
                      <div className="flex flex-col items-center">
                        <FaLock className="text-4xl text-gray-500 mb-4" />
                        <p className="text-gray-400">No hay videos privados disponibles</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Pestaña de Eventos */}
            {activeTab === 'eventos' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {comediante.eventos?.length > 0 ? (
                  comediante.eventos.map((evento) => (
                    <div key={evento.eventoId} className="group">
                      <div className="bg-zinc-800 hover:bg-zinc-700 rounded-xl overflow-hidden transition-all duration-300 shadow-lg">
                        <div className="relative aspect-video">
                          <img 
                            src={evento.imagen} 
                            alt={evento.nombre} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/640x360?text=Sin+Imagen';
                            }}
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-white text-lg font-bold mb-2">
                            {evento.nombre}
                          </h3>
                          <div className="flex flex-col gap-2 text-sm text-gray-400">
                            <span className="flex items-center">
                              <FaCalendarAlt className="mr-2" />
                              {formatDate(evento.fecha)}
                            </span>
                            <span className="flex items-center">
                              <FaMapMarkerAlt className="mr-2" />
                              {evento.lugar}
                            </span>
                          </div>
                          {evento.habilitado && (
                            <div className="mt-3">
                              <span className="inline-block bg-green-600 text-white text-xs px-2 py-1 rounded">
                                Activo
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-12 bg-zinc-800/50 rounded-xl">
                    <p className="text-gray-400">No hay eventos disponibles</p>
                  </div>
                )}
              </div>
            )}

            {/* Pestaña de Livestreams */}
            {activeTab === 'livestreams' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {comediante.livestreams?.length > 0 ? (
                  comediante.livestreams.map((livestream) => (
                    <div key={livestream.livestreamId} className="group">
                      <div className="bg-zinc-800 hover:bg-zinc-700 rounded-xl overflow-hidden transition-all duration-300 shadow-lg">
                        <div className="relative aspect-video">
                          <img 
                            src={livestream.imagen} 
                            alt={livestream.titulo} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/640x360?text=Sin+Imagen';
                            }}
                          />
                          {livestream.enVivo && (
                            <div className="absolute top-2 left-2">
                              <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                                EN VIVO
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="text-white text-lg font-bold mb-2">
                            {livestream.titulo}
                          </h3>
                          <div className="flex flex-col gap-2 text-sm text-gray-400">
                            <span className="flex items-center">
                              <FaCalendarAlt className="mr-2" />
                              {formatDate(livestream.fechaProgramada)}
                            </span>
                            <span className="flex items-center">
                              {livestream.enVivo ? (
                                <span className="text-red-500 font-medium">En vivo ahora</span>
                              ) : (
                                <span>Programado</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-12 bg-zinc-800/50 rounded-xl">
                    <p className="text-gray-400">No hay livestreams disponibles</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComediantePerfilPage;